import React, { JSX, useCallback, useEffect, useRef, useState } from 'react'
import { ArrowPathIcon } from '@heroicons/react/24/outline'
import { LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import { ProviderBrandIcon, TWITCH_BRAND_ICON_SRC } from 'components/ProviderBrandIcon'
import { YouTubeAreaIconLink } from 'components/YouTubeAreaIconLink'
import { useAuth } from 'context/user'
import { graphqlRequest } from 'lib/helpers'
import { triviaYouTubeCounting } from 'lib/triviaPhase'
import {
  TriviaLeaderboard,
  TriviaLeaderboardRow,
  TriviaQuestionStat,
  TriviaSummary
} from 'types/api'
import getTriviaLeaderboardQuery from 'graphql/getTriviaLeaderboard.graphql'

const LIVE_RELOAD_DEBOUNCE_MS = 500

const liveReloadKeyOf = (trivias: TriviaSummary[] | undefined, nowMs: number): string => {
  if (trivias == null) return ''
  return trivias
    .filter((t) => t.revealedAt != null)
    .map((t) => [
      t.triviaId,
      triviaYouTubeCounting(t, nowMs) ? 'counting' : 'final',
      ...t.options.map((o) => `${o.voteCountByPlatform?.twitch ?? o.voteCount}/${o.voteCountByPlatform?.youtube ?? 0}${o.isCorrect === true ? '*' : ''}`)
    ].join(':'))
    .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
    .join('|')
}

const SectionCard = ({
  title,
  children,
  action
}: { title: string, children: React.ReactNode, action?: React.ReactNode }): JSX.Element => (
  <section className='rounded-xl border border-white/5 bg-dm-bg-secondary p-5'>
    <div className='mb-4 flex items-center justify-between'>
      <h3 className='text-base font-semibold text-dm-text-primary'>{title}</h3>
      {action}
    </div>
    {children}
  </section>
)

const includeStaffStorageKey = (eventId: string): string =>
  `sp:triviaLeaderboardIncludeStaff:${eventId}`

const readIncludeStaff = (eventId: string): boolean => {
  try {
    return globalThis.localStorage.getItem(includeStaffStorageKey(eventId)) !== '0'
  } catch {
    return true
  }
}

const writeIncludeStaff = (eventId: string, value: boolean): void => {
  try {
    globalThis.localStorage.setItem(includeStaffStorageKey(eventId), value ? '1' : '0')
  } catch {

  }
}

const StaffTag = ({ row }: { row: TriviaLeaderboardRow }): JSX.Element | null => {
  if (!row.isBroadcaster && !row.isModerator) return null
  return (
    <span className='shrink-0 rounded-full bg-white/5 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-dm-text-accent ring-1 ring-inset ring-white/10'>
      {row.isBroadcaster ? 'Broadcaster' : 'Mod'}
    </span>
  )
}

const LeaderboardTable = ({ title, icon, rows, emptyCopy }: {
  title: string
  icon: JSX.Element
  rows: TriviaLeaderboardRow[]
  emptyCopy: string
}): JSX.Element => (
  <div className='rounded-lg border border-white/5 bg-dm-bg-accent/30 p-3'>
    <div className='mb-2 flex items-center gap-2 text-sm font-medium text-dm-text-primary'>
      {icon}
      {title}
    </div>
    {rows.length === 0
      ? <p className='text-xs text-dm-text-secondary'>{emptyCopy}</p>
      : (
        <ul className='space-y-1'>
          {rows.map((r, i) => (
            <li key={`${r.rank}-${i}`} className='flex items-center justify-between gap-2 text-sm'>
              <span className='flex min-w-0 items-center gap-2'>

                <span className='w-6 shrink-0 text-right text-[11px] tabular-nums text-dm-text-accent'>{r.rank}</span>
                <span className='truncate text-dm-text-secondary'>{r.displayName}</span>
                <StaffTag row={r} />
              </span>
              <span className='shrink-0 tabular-nums text-dm-text-accent'>
                {r.correct} / {r.answered}
              </span>
            </li>
          ))}
        </ul>
        )}
  </div>
)

const QuestionStatLine = ({ stat }: { stat: TriviaQuestionStat }): JSX.Element => (
  <li className='rounded-md border border-white/5 bg-dm-bg-accent/20 px-3 py-2'>
    <div className='mb-1 truncate text-sm text-dm-text-primary' title={stat.question}>{stat.question}</div>
    <div className='flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-dm-text-accent'>
      <span className='inline-flex items-center gap-1.5'>
        <ProviderBrandIcon src={TWITCH_BRAND_ICON_SRC} size='sm' />
        {stat.twitchPct === null
          ? 'No answers'
          : `${stat.twitchPct}% got it right (${stat.twitchCorrect} / ${stat.twitchAnswered})`}
      </span>
      <span className='inline-flex items-center gap-1.5'>
        <YouTubeAreaIconLink size='sm' />

        {stat.youtubeRemoved
          ? 'removed'
          : stat.youtubePct === null
            ? 'No answers'
            : `${stat.youtubePct}% got it right (${stat.youtubeCorrect} / ${stat.youtubeAnswered})`}
      </span>
    </div>
  </li>
)

export const TriviaLeaderboardPanel = ({ eventId, reloadSignal = 0, trivias, nowMs }: {
  eventId: string
  reloadSignal?: number
  trivias?: TriviaSummary[]
  nowMs?: number
}): JSX.Element => {
  const { auth0Token } = useAuth()
  const [includeStaff, setIncludeStaff] = useState<boolean>(() => readIncludeStaff(eventId))
  const [board, setBoard] = useState<TriviaLeaderboard | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const loadSeqRef = useRef(0)

  const load = useCallback(async (): Promise<void> => {
    if (auth0Token === undefined) return
    const seq = ++loadSeqRef.current
    setBusy(true)
    setError(null)
    try {
      const { leaderboard } = await graphqlRequest<{ leaderboard: TriviaLeaderboard | null }>(
        getTriviaLeaderboardQuery, { eventId, includeStaff }, auth0Token
      )
      if (seq === loadSeqRef.current) setBoard(leaderboard)
    } catch (err) {
      if (seq === loadSeqRef.current) setError(err instanceof Error ? err.message : 'Failed to load the leaderboard')
    } finally {
      if (seq === loadSeqRef.current) setBusy(false)
    }
  }, [auth0Token, eventId, includeStaff])

  useEffect(() => { void load() }, [load, reloadSignal])

  const clockMs = nowMs ?? Date.now()
  const liveKey = liveReloadKeyOf(trivias, clockMs)
  const liveKeyRef = useRef(liveKey)
  useEffect(() => {
    if (liveKeyRef.current === liveKey) return
    liveKeyRef.current = liveKey
    const id = globalThis.setTimeout(() => { void load() }, LIVE_RELOAD_DEBOUNCE_MS)
    return () => globalThis.clearTimeout(id)
  }, [liveKey, load])
  const countingRevealed = trivias?.some((t) => t.revealedAt != null && triviaYouTubeCounting(t, clockMs)) ?? false

  const toggleStaff = (value: boolean): void => {
    setIncludeStaff(value)
    writeIncludeStaff(eventId, value)
  }

  return (
    <SectionCard
      title='Leaderboards'
      action={
        <button
          type='button'
          onClick={() => { void load() }}
          disabled={busy}
          title='Refresh leaderboards'
          className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:opacity-60'
        >
          {busy ? <LoadingSpinner /> : <ArrowPathIcon className='size-3.5' aria-hidden='true' />}
          Refresh
        </button>
      }
    >
      <label className='mb-3 flex items-center gap-2 text-xs text-dm-text-secondary'>
        <input
          type='checkbox'
          checked={includeStaff}
          onChange={(e) => toggleStaff(e.target.checked)}
          className='size-4 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
        />
        Include broadcaster and moderators (Twitch board)
      </label>
      {countingRevealed && (
        <p
          data-testid='trivia-leaderboard-counting'
          className='mb-3 flex items-center gap-2 rounded-md border border-cyan-400/20 bg-cyan-500/10 px-3 py-2 text-xs text-cyan-200'
        >
          <LoadingSpinner />
          Counting YouTube answers... The boards reload by themselves when the YouTube count is final.
        </p>
      )}
      {error !== null && (
        <div className='mb-3'>
          <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>
        </div>
      )}
      {board === null
        ? (
          <p className='rounded-md border border-dashed border-white/10 px-3 py-6 text-center text-xs text-dm-text-secondary'>
            {busy ? 'Loading the leaderboards…' : 'No leaderboard data yet.'}
          </p>
          )
        : board.revealedQuestionCount === 0
          ? (
            <p className='rounded-md border border-dashed border-white/10 px-3 py-6 text-center text-xs text-dm-text-secondary'>
              The leaderboard fills in as questions are revealed. Only revealed
              questions score.
            </p>
            )
          : (
            <>
              <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                <LeaderboardTable
                  title='Twitch'
                  icon={<ProviderBrandIcon src={TWITCH_BRAND_ICON_SRC} size='sm' />}
                  rows={board.twitch}
                  emptyCopy='No Twitch answers on revealed questions yet.'
                />
                <LeaderboardTable
                  title='YouTube'
                  icon={<YouTubeAreaIconLink size='sm' />}
                  rows={board.youtube}
                  emptyCopy='No YouTube answers on revealed questions yet.'
                />
              </div>
              <p className='mt-2 text-[10px] text-dm-text-accent'>
                Scores show correct / answered across {board.revealedQuestionCount} revealed
                question{board.revealedQuestionCount === 1 ? '' : 's'}. Ties share a rank.
                The two boards are separate by design and are never combined.
              </p>
              {board.questions.length > 0 && (
                <div className='mt-4'>
                  <div className='mb-2 text-xs font-medium uppercase tracking-wider text-dm-text-accent'>
                    Per question
                  </div>
                  <ul className='space-y-1.5'>
                    {board.questions.map((q) => <QuestionStatLine key={q.triviaId} stat={q} />)}
                  </ul>
                </div>
              )}
            </>
            )}
    </SectionCard>
  )
}
